import { Axios, AxiosError, AxiosResponse, AxiosResponseHeaders } from 'axios';
import { AttachmentUploadIntentStatus, AuthorizedUploadIntent } from '@client/features/attachments/types';
import { container } from '@client/core/container';
import { User } from '@client/features/user/models/user';
import { HttpException } from '@client/core/exception/http-exception';

/**
 * Log details about an error during the file attachment workflow. Specifically
 * the error occurred after we received the file.
 */
export async function reportAttachmentErrorToIntent(opts: {
  workspaceOwnerId: User['id'];
  intent: AuthorizedUploadIntent;
  workflowStatus: AttachmentUploadIntentStatus;
  error: AxiosError | HttpException | Error | any;
}): Promise<AxiosResponse> {
  const api = container(Axios);
  const { workspaceOwnerId, intent, workflowStatus, error } = opts;

  let response: AxiosResponse | null = null;
  let message = 'Unknown Error';
  let statusCode = 0;
  let headers: AxiosResponseHeaders | null = null;
  let data: any = null;

  // We can unpack multiple types of errors depending on what happened
  if (error instanceof HttpException) {
    // Error when our HTTP API communication library encountered an error response
    response = error.originalResponse;

    message = 'HttpException: ' + error.message;
    statusCode = response?.status || 0;
    headers = response?.headers || null;
    data = response?.data || 'No data in HttpException';
  } else if (error instanceof AxiosError) {
    // Error when a general HTTP communication error occurred that didn't
    // originate from our HTTP API communication
    response = error.response;

    message = 'AxiosError: ' + (error.response?.statusText || error.message || message);
    statusCode = response?.status || 0;
    headers = response?.headers || null;
    data = response?.data || 'No data in AxiosError';
  } else if (error instanceof Error) {
    message = 'JS Error: ' + error.message;
    statusCode = 0;
    data = error;
  }

  return await api.put(`/workspace/${workspaceOwnerId}/upload-intent/${intent.uuid}/error`, {
    status: workflowStatus,
    errorContext: {
      response: {
        message,
        statusCode,
        headers,
        data,
      },
    },
  });
}
